/**
 * Size options utilities for cold beverages.
 * Large/Regular sizes are only shown for items with isCold === true.
 * - Large = the default/base size, priced at item.price
 * - Regular = the smaller, optional size, priced at item.sizes.regular
 */

/**
 * Get available size options for a menu item.
 * @param {Object} item - Menu item with isCold, price, and optional sizes.regular
 * @returns {Array} Array of size option objects: { value, label, price, disabled, tooltip }
 */
export function getSizeOptions(item) {
  if (!item?.isCold) return [];
  
  const largePrice = Number(item.price) || 0;
  const regularPrice = Number(item?.sizes?.regular || item?.sizes?.small) || 0;
  
  return [
    { 
      value: 'large', 
      label: 'Large', 
      price: largePrice, 
      disabled: false,
      tooltip: null
    },
    { 
      value: 'regular', 
      label: 'Regular', 
      price: regularPrice, 
      disabled: regularPrice <= 0,
      tooltip: regularPrice > 0 ? null : 'Coming soon - regular size not yet available'
    }
  ];
}

/**
 * Get the price for a specific size of a menu item.
 * @param {Object} item - Menu item with price and optional sizes.regular
 * @param {string} size - 'large' or 'regular'
 * @returns {number} Price for the selected size
 */
export function priceForSize(item, size) {
  if (size === 'regular') {
    const regPrice = Number(item?.sizes?.regular || item?.sizes?.small);
    if (regPrice > 0) return regPrice;
  }
  return Number(item?.price) || 0;
}

/**
 * Check if an item is a cold beverage with size options.
 * @param {Object} item - Menu item
 * @returns {boolean}
 */
export function hasSizeOptions(item) {
  return item?.isCold === true;
}

/**
 * Check if regular (smaller) size is available (has a valid price).
 * @param {Object} item - Menu item
 * @returns {boolean}
 */
export function isRegularSizeAvailable(item) {
  return Number(item?.sizes?.regular || item?.sizes?.small) > 0;
}

/**
 * Get display label for a size value.
 * @param {string} size - 'large' or 'regular'
 * @returns {string}
 */
export function getSizeLabel(size) {
  const labels = {
    large: 'Large',
    regular: 'Regular',
    // Legacy mappings for historical orders
    small: 'Regular',
    standard: 'Large',
    medium: 'Large'
  };
  return labels[size] || 'Large';
}

const sizeOptionsExports = {
  getSizeOptions,
  priceForSize,
  hasSizeOptions,
  isRegularSizeAvailable,
  getSizeLabel
};

export default sizeOptionsExports;
